import _ from 'lodash';
import React from 'react';

import {formatDate} from 'utils/datetime';

import {BOOKING_TYPE} from 'ducks/bookings';
import Money from 'components/Money';
import Avatar from 'components/Avatar';
import BookingSetStatusTag from 'components/BookingSetStatusTag';
import {DetailTenantInfo} from 'components/TenantInfo';
import AvatarImageWithText from 'components/AvatarImageWithText';

import './styles.css';

interface MemberProps {
  id: number;
  fullName: string;
  email: string;
  initials: string;
  avatarCropped: string | undefined;
  internalAvatarCropped: string | undefined;
}

interface PrimaryTenantBoxProps {
  primaryTenant: MemberProps;
  members: Array<MemberProps>;
  currentRentAndOneOffBalance: number;
  currentDepositBalance: number;
  startDate: string;
  endDate: string;
  stayDuration: string;
  bookingTypes: Array<string>;
  status: string;
}

const BOOKING_TYPES_INITIALS_MAPPING = {
  [BOOKING_TYPE.GUEST]: 'G',
  [BOOKING_TYPE.CORPORATE]: 'C',
  [BOOKING_TYPE.LONG_STAY]: 'LS',
  [BOOKING_TYPE.EXTENDED_STAY]: 'ES'
};

const PrimaryTenantBox = ({
  primaryTenant,
  members,
  currentRentAndOneOffBalance,
  currentDepositBalance,
  startDate,
  endDate,
  stayDuration,
  status,
  bookingTypes = []
}: PrimaryTenantBoxProps) => {
  const bookingTypeInitials =
    BOOKING_TYPES_INITIALS_MAPPING[
      _.first(bookingTypes) || BOOKING_TYPE.LONG_STAY
    ];

  return (
    <div className="PrimaryTenantBox colab-medium-border-radius">
      <div className="PrimaryTenantBox__header">
        <AvatarImageWithText
          initials={bookingTypeInitials}
          text={bookingTypes.join()}
        />
      </div>
      <div className="colab-sidepadding-2">
        <div className="PrimaryTenantBox__infoSection colab-centered-v">
          <Avatar
            className="PrimaryTenantBox__infoSection__avatar"
            url={
              primaryTenant.internalAvatarCropped || primaryTenant.avatarCropped
            }
            initials={primaryTenant.initials}
          />
          <div className="PrimaryTenantBox__infoSection__name colab-bold">
            {primaryTenant.fullName}
          </div>
          <div className="PrimaryTenantBox__infoSection__email colab-bold">
            {primaryTenant.email}
          </div>

          <div className="PrimaryTenantBox__infoSection__role">
            Primary Tenant
          </div>

          <div className="PrimaryTenantBox__infoSection__status">
            <BookingSetStatusTag status={status} />
          </div>
        </div>
      </div>

      <div className="PrimaryTenantBox__bookingSetInfo">
        <div className="PrimaryTenantBox__balanceSection colab-sidepadding-2">
          <div>Current balance</div>
          <div>
            <Money balance amount={currentRentAndOneOffBalance} />
          </div>
        </div>
        <div className="PrimaryTenantBox__balanceSection colab-sidepadding-2">
          <div>Deposit balance</div>
          <div>
            <Money balance amount={currentDepositBalance} />
          </div>
        </div>
        {!_.isEmpty(members) && (
          <div className="PrimaryTenantBox__membersSection colab-sidepadding-2">
            <div>Members </div>
            {members.map(member => (
              <DetailTenantInfo key={member.id} tenant={member} />
            ))}
          </div>
        )}
        <div className="PrimaryTenantBox__durationSection colab-sidepadding-2">
          <div>Stay duration</div>
          {stayDuration && (
            <React.Fragment>
              <div>
                {formatDate(startDate)} - {formatDate(endDate)}
              </div>
              <div>{stayDuration}</div>
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrimaryTenantBox;
